import React from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import RequireApproval from './components/RequireApproval';
import RequireAdmin from './components/RequireAdmin';
import AdminLayout from './components/AdminLayout';
import Dashboard from './components/Dashboard';
import PendingApproval from './components/PendingApproval';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminOrders from './pages/admin/Orders';
import AdminProducts from './pages/admin/Products';
import AdminSettings from './pages/admin/Settings';
import AdminDisplayCases from './pages/admin/DisplayCases';
import DisplayCaseProgram from './pages/DisplayCaseProgram';
import OrderHistory from './pages/OrderHistory';
import { useCart } from './hooks/useCart';
import {
  ShieldCheck,
  Store,
  Truck,
  BadgePercent,
} from 'lucide-react';

/**
 * Skip the "account recovery requires verification" screen.
 * Email is already auto-verified by Cognito so this prompt is unnecessary.
 */
function SkipVerify() {
  const { skipVerification } = useAuthenticator();
  React.useEffect(() => {
    skipVerification();
  }, []);
  return null;
}

function AuthenticatedApp({ signOut, user }: { signOut: (() => void) | undefined; user: any }) {
  const cart = useCart();

  return (
    <Layout user={user} signOut={signOut}>
      <Routes>
        <Route
          path="/"
          element={
            <RequireApproval
              user={user}
              pendingComponent={<PendingApproval user={user} />}
            >
              <Dashboard user={user} cart={cart} />
            </RequireApproval>
          }
        />
        <Route
          path="/orders"
          element={
            <RequireApproval
              user={user}
              pendingComponent={<PendingApproval user={user} />}
            >
              <OrderHistory cart={cart} />
            </RequireApproval>
          }
        />
        <Route
          path="/display-cases"
          element={
            <RequireApproval
              user={user}
              pendingComponent={<PendingApproval user={user} />}
            >
              <DisplayCaseProgram />
            </RequireApproval>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="display-cases" element={<AdminDisplayCases />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
    </Layout>
  );
}

const features = [
  {
    icon: BadgePercent,
    title: 'Wholesale Pricing',
    desc: 'Exclusive discounts on all products',
  },
  {
    icon: Store,
    title: 'Display Case Program',
    desc: 'Earn rebates showcasing our brand',
  },
  {
    icon: Truck,
    title: 'Fast Fulfillment',
    desc: 'Direct orders from our warehouse',
  },
  {
    icon: ShieldCheck,
    title: 'Authorized Retailers',
    desc: 'Verified business accounts only',
  },
];

function AmbientGlow() {
  return (
    <div className="auth-ambient" aria-hidden="true">
      <div className="auth-ambient-blob auth-ambient-blob-1" />
      <div className="auth-ambient-blob auth-ambient-blob-2" />
      <div className="auth-ambient-blob auth-ambient-blob-3" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Authenticator
        signUpAttributes={['name', 'email']}
        formFields={{
          signUp: {
            name: {
              label: 'Full Name',
              placeholder: 'Enter your full name',
              order: 1,
            },
            email: {
              label: 'Email Address',
              placeholder: 'Enter your business email',
              order: 2,
            },
            password: {
              label: 'Password',
              placeholder: 'Create a password',
              order: 3,
            },
            confirm_password: {
              label: 'Confirm Password',
              placeholder: 'Confirm your password',
              order: 4,
            },
            'custom:company_name': {
              label: 'Company Name',
              placeholder: 'Enter your business name',
              order: 5,
            },
            'custom:tax_id': {
              label: 'Tax ID / EIN',
              placeholder: 'Enter your Tax ID or EIN',
              order: 6,
            },
            'custom:access_code': {
              label: 'Access Code (Optional)',
              placeholder: 'Enter access code for instant approval',
              order: 7,
              isRequired: false,
            },
          },
        }}
        components={{
          Header() {
            return (
              <div className="auth-header-wrapper">
                {/* Left branding panel */}
                <div className="auth-brand-panel">
                  <AmbientGlow />

                  <div className="auth-brand-content">
                    {/* Logo */}
                    <div className="auth-brand-logo">
                      <img
                        src="/top-dog-logo.png"
                        alt="Top Dog Brand Inc."
                        className="auth-logo-img"
                      />
                      <div className="auth-brand-divider" />
                      <p className="text-text-muted uppercase tracking-[0.3em] text-[10px] mt-4 font-medium">
                        Wholesale Portal
                      </p>
                    </div>

                    <p className="auth-brand-tagline">
                      Premium leather goods for authorized retailers.
                      Sign in or create an account to access exclusive wholesale pricing and programs.
                    </p>

                    {/* Feature grid */}
                    <div className="auth-features-grid">
                      {features.map((f, i) => (
                        <div key={i} className="auth-feature-item">
                          <div className="auth-feature-icon">
                            <f.icon className="w-4 h-4 text-accent" />
                          </div>
                          <div>
                            <p className="text-cream text-sm font-medium">{f.title}</p>
                            <p className="text-text-muted text-xs mt-0.5">{f.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="auth-brand-footer">
                    <p className="text-text-muted text-xs">
                      &copy; {new Date().getFullYear()} Top Dog Brand. All rights reserved.
                    </p>
                  </div>
                </div>

                {/* Right panel header - shows above the form */}
                <div className="auth-form-header">
                  <div className="auth-form-header-inner">
                    <img
                      src="/top-dog-logo.png"
                      alt="Top Dog Brand Inc."
                      className="auth-logo-img-mobile"
                    />
                    <h2 className="text-2xl font-display text-cream tracking-wide">
                      TOP DOG BRAND
                    </h2>
                    <p className="text-text-muted text-sm mt-1.5 max-w-xs mx-auto md:mx-0">
                      Sign in to your wholesale account
                    </p>
                  </div>
                </div>
              </div>
            );
          },
          Footer() {
            return (
              <div className="auth-form-footer">
                <p className="text-text-muted text-xs text-center">
                  Need help?{' '}
                  <a
                    href="mailto:wholesale@topdogbrand.com"
                    className="text-accent hover:text-accent-light underline underline-offset-2 transition-colors"
                  >
                    Contact our wholesale team
                  </a>
                </p>
                {/* Mobile-only copyright */}
                <p className="auth-mobile-copyright text-text-muted text-xs text-center mt-4">
                  &copy; {new Date().getFullYear()} Top Dog Brand
                </p>
              </div>
            );
          },
          VerifyUser: {
            Header: SkipVerify,
          },
          ConfirmVerifyUser: {
            Header: SkipVerify,
          },
        }}
      >
        {({ signOut, user }) => (
          <AuthenticatedApp signOut={signOut} user={user} />
        )}
      </Authenticator>
    </BrowserRouter>
  );
}

export default App;
